import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CHAT_POLL_INTERVAL_MS = 5000;
const CHAT_Z_INDEX = 9998;

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const messagesEndRef = useRef(null);
  const apiRef = useRef(API);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const loadMessages = useCallback(async (convId) => {
    try {
      const response = await axios.get(`${apiRef.current}/chat/messages/${convId}`);
      setMessages(response.data);
    } catch (error) {
      // Non-critical - chat will show empty state, log in dev for debugging
      if (process.env.NODE_ENV === 'development') {
        console.debug('Failed to load chat messages:', error.message);
      }
    }
  }, []);

  useEffect(() => {
    // Load conversation ID from localStorage
    const savedConvId = localStorage.getItem('chatConversationId');
    const savedName = localStorage.getItem('chatCustomerName');
    const savedEmail = localStorage.getItem('chatCustomerEmail');
    
    if (savedConvId) {
      setConversationId(savedConvId);
      setCustomerName(savedName || '');
      setCustomerEmail(savedEmail || '');
      setIsInitialized(true);
      loadMessages(savedConvId);
    }
  }, [loadMessages]);

  useEffect(() => {
    if (isOpen && conversationId) {
      const interval = setInterval(() => {
        loadMessages(conversationId);
      }, CHAT_POLL_INTERVAL_MS);
      
      return () => clearInterval(interval);
    }
  }, [isOpen, conversationId, loadMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const initializeChat = useCallback(() => {
    if (!customerName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    const newConvId = `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setConversationId(newConvId);
    setIsInitialized(true);

    // Save to localStorage
    localStorage.setItem('chatConversationId', newConvId);
    localStorage.setItem('chatCustomerName', customerName);
    localStorage.setItem('chatCustomerEmail', customerEmail);
  }, [customerName, customerEmail]);

  const sendMessage = useCallback(async () => {
    if (!newMessage.trim() || !conversationId) return;

    const messageData = {
      conversation_id: conversationId,
      sender: 'customer',
      sender_name: customerName,
      sender_email: customerEmail || null,
      message: newMessage.trim(),
    };

    try {
      const response = await axios.post(`${apiRef.current}/chat/messages`, messageData);
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch {
      toast.error('Failed to send message');
    }
  }, [newMessage, conversationId, customerName, customerEmail]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isInitialized) {
        sendMessage();
      } else {
        initializeChat();
      }
    }
  }, [isInitialized, sendMessage, initializeChat]);

  return (
    <>
      {/* Chat Button - Smaller on mobile, respects safe area */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="chat-widget-button"
          className="fixed bottom-5 right-4 w-11 h-11 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-[#E85D75] text-white rounded-full shadow-lg shadow-[#E85D75]/25 hover:bg-[#D64560] transition-all hover:scale-105 active:scale-95 flex items-center justify-center"
          style={{ zIndex: CHAT_Z_INDEX, marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <MessageCircle className="w-5 h-5 lg:w-6 lg:h-6" />
        </button>
      )}

      {/* Chat Window - Full width on mobile */}
      {isOpen && (
        <div
          data-testid="chat-widget-window"
          className="fixed bottom-3 right-3 left-3 sm:left-auto sm:right-4 sm:bottom-4 sm:w-[360px] h-[420px] sm:h-[460px] bg-white rounded-2xl shadow-2xl flex flex-col border border-stone-200"
          style={{ zIndex: CHAT_Z_INDEX, marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          {/* Header */}
          <div className="bg-[#E85D75] text-white p-3 sm:p-4 rounded-t-2xl flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-lg">KittenState</h3>
              <p className="text-sm text-white/80">We typically reply within minutes</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-stone-50">
            {!isInitialized ? (
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-xl border border-stone-100">
                  <p className="text-stone-700 mb-4">
                    Hi! 👋 Have questions about our Scottish kittens? We're here to help!
                  </p>
                  <Input
                    placeholder="Your name *"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="mb-2"
                    data-testid="chat-name-input"
                  />
                  <Input
                    placeholder="Email (optional)"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="mb-3"
                  />
                  <Button
                    onClick={initializeChat}
                    className="w-full bg-[#E85D75] hover:bg-[#D64560] text-white rounded-full"
                    data-testid="chat-start-button"
                  >
                    Start Chat
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {messages.length === 0 && (
                  <div className="bg-white p-4 rounded-xl border border-stone-100 text-center">
                    <p className="text-stone-600">
                      Welcome {customerName}! How can we help you today?
                    </p>
                  </div>
                )}
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender === 'customer' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] p-3 rounded-2xl ${
                        msg.sender === 'customer'
                          ? 'bg-[#E85D75] text-white rounded-br-sm'
                          : 'bg-white text-stone-700 border border-stone-100 rounded-bl-sm'
                      }`}
                    >
                      {msg.sender === 'admin' && (
                        <p className="text-xs font-semibold mb-1 text-[#E85D75]">KittenState</p>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className="text-xs mt-1 opacity-70">
                        {new Date(msg.created_at).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Area */}
          {isInitialized && (
            <div className="p-4 border-t border-stone-200 bg-white rounded-b-2xl">
              <div className="flex gap-2">
                <Input
                  placeholder="Type your message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="flex-1"
                  data-testid="chat-message-input"
                />
                <Button
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                  className="bg-[#E85D75] hover:bg-[#D64560] text-white rounded-full px-4"
                  data-testid="chat-send-button"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
